import { ReactElement, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Language, Localizable } from '@breathelife/types';
import { AutocompleteOption, Box, SimpleCheckbox } from '@breathelife/ui-components';
import {
  IconName,
  PdfDocumentType,
  QuestionnaireBlueprintCopyableOption,
  QuestionnaireBlueprintCopyableOptions,
  SubsectionBlueprint,
  SubsectionPartIdentifier,
} from '@zinnia/form-engine-sdk';

import { QuestionnaireNodeIds } from '../../../../../Helpers/questionnaireEditor/questionnaireNodeIds';
import { useCarrierContext } from '../../../../../Hooks';
import { TextInput } from '../../../../../Pages/Admin/Questionnaire/QuestionnaireEditor/Components/TextInput';
import { SetSelectedPartIdentifier } from '../../../../../Pages/Admin/Questionnaire/QuestionnaireEditor/types';
import { useUpdateQuestionnaireElementBlueprint } from '../../../../../ReactQuery/Admin/Questionnaire/questionnaireVersion.mutations';
import { QuestionnaireVersionDataContext } from '../../ContextProvider/QuestionnaireVersionDataContextProvider';
import { AdvancedBlueprintOptions } from '../Components/AdvancedBlueprintOptions';
import { IconNameSelector } from '../Components/IconNameSelector';
import { useAdvancedBlueprintOptions } from '../Hooks/useAdvancedBlueprintOptions';
import { useHighlightedTextInBlueprintElement } from '../Hooks/useHighlightedTextInBlueprintElement';
import { ConditionsEditor } from './ConditionsEditor';

type Props = {
  blueprint: SubsectionBlueprint;
  sectionGroupCollectionNodeId: string | undefined;
  questionnaireNodeIds: QuestionnaireNodeIds;
  dataLabelOptions: AutocompleteOption[];
  partIdentifier: SubsectionPartIdentifier;
  setSelectedPartIdentifier: SetSelectedPartIdentifier;
  selectedLanguage: Language;
  parentHidden: boolean;
  isEditingEnabled: boolean;
  disableCopyable: boolean;
  parentPdfDocumentTypes: PdfDocumentType[];
};

export function SubsectionBlueprintEditor(props: Props): ReactElement {
  const {
    partIdentifier,
    blueprint,
    sectionGroupCollectionNodeId,
    selectedLanguage,
    parentHidden,
    isEditingEnabled,
    questionnaireNodeIds,
    dataLabelOptions,
    disableCopyable,
    parentPdfDocumentTypes,
  } = props;

  const { t } = useTranslation();
  const { showBlueprintIdInEditor } = useCarrierContext();

  const [referenceLabel, setReferenceLabel] = useState<string>('');
  const [title, setTitle] = useState<Partial<Localizable>>({});
  const [text, setText] = useState<Partial<Localizable>>({});
  const [iconName, setIconName] = useState<IconName | undefined>();
  const [nextStepButtonText, setNextStepButtonText] = useState<Partial<Localizable>>({});
  const [showInNavigation, setShowInNavigation] = useState(false);
  const [pageBreakSubSectionInPdf, setPageBreakSubSectionInPdf] = useState(false);
  const { questionnaireVersionId } = useContext(QuestionnaireVersionDataContext);

  const blueprintUpdate = useUpdateQuestionnaireElementBlueprint(questionnaireVersionId);

  const {
    actions: { setPlatforms, setRenderOn, setPdfDocuments, setDataLabel, setCopyable },
    selectors: { platforms, renderOn, pdfDocuments, dataLabel, copyable },
  } = useAdvancedBlueprintOptions<SubsectionPartIdentifier>({
    partIdentifier,
    blueprintUpdate,
  });

  const [isInitialized, setIsInitialized] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isInitialized) return;

    const referenceLabel = blueprint.referenceLabel;
    if (referenceLabel) {
      setReferenceLabel(referenceLabel);
    }

    const initialTitle = blueprint.title;
    if (initialTitle) {
      setTitle(initialTitle);
    }

    const initialText = blueprint.text;
    if (initialText) {
      setText(initialText);
    }

    const initialIconName = blueprint.iconName;
    if (initialIconName) {
      setIconName(initialIconName);
    }

    const initialNextStepButtonText = blueprint.nextStepButtonText;
    if (initialNextStepButtonText) {
      setNextStepButtonText(initialNextStepButtonText);
    }

    const initialShowInNavigation = blueprint.showInNavigation;
    if (initialShowInNavigation) {
      setShowInNavigation(initialShowInNavigation);
    }

    const initialPageBreakSubSectionInPdf = blueprint.pageBreakSubSectionInPdf;
    if (initialPageBreakSubSectionInPdf) {
      setPageBreakSubSectionInPdf(initialPageBreakSubSectionInPdf);
    }

    const initialDataLabel = blueprint.dataLabel;
    if (initialDataLabel) {
      setDataLabel(initialDataLabel, false);
    } else if (dataLabelOptions?.length) {
      setDataLabel(dataLabelOptions[0].value, false);
    }

    setPlatforms(blueprint.platforms ?? [], false);
    setRenderOn(blueprint.renderOn ?? [], false);
    setPdfDocuments(blueprint.pdfDocuments ?? [], false);
    setCopyable(blueprint.copyable ?? QuestionnaireBlueprintCopyableOptions.none, false);

    setIsInitialized(true);
  }, [
    blueprint.title,
    blueprint.text,
    blueprint.iconName,
    blueprint.nextStepButtonText,
    blueprint.showInNavigation,
    blueprint.dataLabel,
    blueprint.platforms,
    blueprint.renderOn,
    blueprint.pdfDocuments,
    blueprint.copyable,
    blueprint.referenceLabel,
    blueprint.pageBreakSubSectionInPdf,
    dataLabelOptions,
    setPlatforms,
    setRenderOn,
    setPdfDocuments,
    isInitialized,
    setDataLabel,
    setCopyable,
  ]);

  const disabled = parentHidden || !!blueprint.hidden || !isEditingEnabled;

  const highlightedTexts = useHighlightedTextInBlueprintElement({ partIdentifier, blueprint });

  const onReferenceLabelBlur = useCallback(() => {
    void blueprintUpdate({
      partIdentifier,
      update: {
        property: 'referenceLabel',
        value: referenceLabel.trim(),
        blueprintId: blueprint.id,
      },
    });
  }, [blueprintUpdate, partIdentifier, referenceLabel, blueprint.id]);

  return (
    <Box ref={containerRef}>
      {showBlueprintIdInEditor && (
        <Box pb={1}>
          <TextInput label={'Id'} value={blueprint?.id} disabled highlighted={!!highlightedTexts?.blueprintId} />
        </Box>
      )}

      <TextInput
        label={t('admin.questionnaireManagement.input.referenceLabel')}
        value={referenceLabel}
        disabled={disabled}
        onChange={(event) => {
          setReferenceLabel(event.target.value);
        }}
        onBlur={onReferenceLabelBlur}
        highlighted={!!highlightedTexts?.referenceLabel}
      />

      <TextInput
        highlighted={!!highlightedTexts.title}
        label={t('admin.questionnaireManagement.input.subsectionTitle')}
        value={title[selectedLanguage] ?? ''}
        disabled={disabled}
        onChange={(event) => {
          const value = event.target.value;
          setTitle((previousValue) => ({ ...previousValue, [selectedLanguage]: value }));
        }}
        onBlur={() => {
          void blueprintUpdate({
            partIdentifier,
            update: { property: 'title', value: title },
          });
        }}
      />
      <Box pt={1}>
        <TextInput
          highlighted={!!highlightedTexts.text}
          label={t('admin.questionnaireManagement.input.subsectionText')}
          value={text[selectedLanguage] ?? ''}
          disabled={disabled}
          onChange={(event) => {
            const value = event.target.value;
            setText((previousValue) => ({ ...previousValue, [selectedLanguage]: value }));
          }}
          multiline={true}
          onBlur={() => {
            void blueprintUpdate({
              partIdentifier,
              update: { property: 'text', value: text },
            });
          }}
        />
      </Box>
      <Box pt={1}>
        <IconNameSelector
          label={t('admin.questionnaireManagement.input.iconName')}
          disabled={disabled}
          value={iconName}
          onChange={(newIconName) => {
            setIconName(newIconName);

            void blueprintUpdate({
              partIdentifier,
              update: { property: 'iconName', value: newIconName },
            });
          }}
        />
      </Box>
      <Box pt={1}>
        <TextInput
          label={t('admin.questionnaireManagement.input.subsectionNextStepButtonText')}
          value={nextStepButtonText[selectedLanguage] ?? ''}
          disabled={disabled}
          onChange={(event) => {
            const value = event.target.value;
            setNextStepButtonText((previousValue) => ({ ...previousValue, [selectedLanguage]: value }));
          }}
          multiline={true}
          onBlur={() => {
            void blueprintUpdate({
              partIdentifier,
              update: { property: 'nextStepButtonText', value: nextStepButtonText },
            });
          }}
        />
      </Box>
      <Box pt={2}>
        <SimpleCheckbox
          id={`showInNavigation-${blueprint.partName}`}
          label={t('admin.questionnaireManagement.input.showInNavigation')}
          checked={showInNavigation}
          disabled={disabled}
          onChange={(event) => {
            setShowInNavigation(event.target.checked);
            void blueprintUpdate({
              partIdentifier,
              update: { property: 'showInNavigation', value: event.target.checked },
            });
          }}
        />
      </Box>
      <Box pt={2}>
        <SimpleCheckbox
          id={`pageBreakSubSectionInPdf`}
          label={t('admin.questionnaireManagement.input.pageBreakSubSectionInPdf')}
          checked={pageBreakSubSectionInPdf}
          onChange={(event) => {
            setPageBreakSubSectionInPdf(event.target.checked);
            void blueprintUpdate({
              partIdentifier,
              update: { property: 'pageBreakSubSectionInPdf', value: event.target.checked },
            });
          }}
          disabled={disabled}
        />
      </Box>

      <Box pt={1} pb={2} mr={-1}>
        <ConditionsEditor
          questionnaireNodeIds={questionnaireNodeIds}
          condition={blueprint?.visible}
          collectionContext={sectionGroupCollectionNodeId ? [sectionGroupCollectionNodeId] : []}
          isReadOnly={parentHidden || !!blueprint.hidden || disabled}
          label={t('admin.questionnaireManagement.rules.visibility.conditions')}
          editHeadingText={t('admin.questionnaireManagement.rules.visibility.edit')}
          selectedLanguage={selectedLanguage}
          saveCondition={(condition) => {
            void blueprintUpdate({
              partIdentifier,
              update: { property: 'visible', value: condition },
            });
          }}
        />
      </Box>

      <AdvancedBlueprintOptions
        tag='subsection'
        platform={{ selected: platforms, onChange: setPlatforms }}
        renderOn={{ selected: renderOn, onChange: setRenderOn }}
        pdfDocument={{ selected: pdfDocuments, onChange: setPdfDocuments, parentTypes: parentPdfDocumentTypes }}
        dataLabel={{
          selected: dataLabel,
          options: dataLabelOptions,
          onChange: (value) => {
            if (value !== null) {
              setDataLabel(value);
            }
          },
        }}
        copyable={{
          selected: copyable,
          disabled: disableCopyable,
          onChange: (value) => {
            if (value !== null) {
              setCopyable(value as QuestionnaireBlueprintCopyableOption);
            }
          },
        }}
        disabled={disabled}
      />
    </Box>
  );
}
